import type { AgencyAlertKind, IAgencyFinding } from './agencyDigest';

/**
 * Words that mark a finding as something that already happened to the client rather than something
 * drifting. There is no incident taxonomy on the API, so this reads the text the model wrote; it can
 * only ever be a hint, which is why it refines a structural classification instead of replacing one.
 */
export const INCIDENT_TERMS = [
    'er visit', 'emergency room', 'emergency department', 'ed visit',
    'hospital', 'hospitalized', 'hospitalised', 'admitted', 'admission',
    'ambulance', 'paramedic', '911',
    'fall', 'fell', 'fracture', 'injury', 'injured',
    'incident'
];

/**
 * Whether any term appears in the text as the START of a word.
 *
 * A plain substring test is wrong here and was actively harmful: "er visit" is in the incident list
 * to catch an ER visit, and it matched "fewer visits", so a note that someone was getting FEWER
 * visitors was reported as an emergency. Anchoring the front of each term fixes that while still
 * catching the ends of words, which is what the list wants: "hospital" has to reach "hospitalised",
 * and "fall" has to reach "fallen".
 */
export const mentionsAny = (text: string, terms: string[]): boolean => {
    const haystack = text.toLowerCase();
    return terms.some(term => new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(haystack));
};

const mentionsIncident = (finding: IAgencyFinding): boolean => {
    return mentionsAny(`${finding.title} ${finding.narrative}`, INCIDENT_TERMS);
};

/**
 * The finding's own type decides the kind, and incident wording can promote an anomaly. A gap stays
 * an ordinary observation whatever words describe it: letting the text override that would put
 * "no DNR on file after her fall" in the incident column, where nobody would go looking for a
 * missing document.
 */
export const classifyFinding = (finding: IAgencyFinding): AgencyAlertKind => {
    if (finding.type === 'gap') {
        return 'observation';
    }
    if (finding.type === 'anomaly') {
        return mentionsIncident(finding) ? 'incident' : 'baseline';
    }
    return 'observation';
};
